import { useSyncExternalStore, useEffect, useState, type ReactNode } from 'react';
import { useLocation } from 'wouter';
import { canonicalPath } from '@workspace/url-source';
import { cmsPageByPath, serializeCmsJson, type CmsField, type CmsSnapshot } from '@workspace/url-source/cms';
import { isPrerenderMode } from '@/lib/isPrerenderMode';

let snapshot: CmsSnapshot | null = null;
const listeners = new Set<() => void>();
const subscribe = (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener); }; };
export function getCmsSnapshot(): CmsSnapshot | null { return snapshot; }
export function setCmsSnapshot(value: CmsSnapshot | null): void {
  snapshot = value;
  listeners.forEach(listener => listener());
}
export function initializeCms(): void {
  const element = document.getElementById('bukietowa-cms-data');
  // Cross-page CMS navigation gets the current document AND its matching JS build.
  // Otherwise a long-lived tab could render a new revision with an old template.
  document.addEventListener('click', event => {
    if (snapshot?.preview || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const anchor = (event.target as Element)?.closest?.('a');
    if (!anchor || anchor.target || anchor.hasAttribute('download')) return;
    const target = new URL(anchor.href, window.location.href);
    if (target.origin === window.location.origin && target.pathname !== window.location.pathname && cmsPageByPath(target.pathname)) {
      event.preventDefault(); window.location.assign(target.href);
    }
  }, true);
  if (!element?.textContent) return;
  // A bad seed must not silently revert a published page to compiled defaults.
  const value = JSON.parse(element.textContent) as CmsSnapshot;
  if (value.schemaVersion !== 1 || !cmsPageByPath(value.path) || !value.data || typeof value.data !== 'object') throw new Error('Nieprawidłowa wersja treści strony.');
  setCmsSnapshot(value);
  if (value.preview) {
    document.addEventListener('submit', event => { event.preventDefault(); event.stopImmediatePropagation(); }, true);
    document.addEventListener('click', event => {
      if ((event.target as Element)?.closest?.('a')) event.preventDefault();
    }, true);
  }
}
export function cmsPublicPath(): string {
  return snapshot?.preview ? snapshot.path : window.location.pathname;
}
export function cmsPreviewLocation(): [string, (to: string) => void] {
  const path = getCmsSnapshot()?.path ?? '/';
  return [path === '/' ? '/' : path.replace(/\/$/, ''), () => {}];
}

export function useCmsFields(fields: CmsField[]) {
  const current = useSyncExternalStore(subscribe, getCmsSnapshot, () => null);
  const defaults = new Map(fields.map(field => [field.key, field.defaultValue]));
  return {
    fields,
    value(key: string): string { return current?.data[key] ?? defaults.get(key) ?? ''; },
    text: (key: string) => ({ 'data-cms-text': key }),
    image: (key: string) => ({ 'data-cms-image': key }),
    alt: (key: string) => ({ 'data-cms-alt': key }),
  };
}
export function CmsSchema({ fields }: { fields: CmsField[] }) {
  return <script type="application/json" data-cms-fields="1" dangerouslySetInnerHTML={{ __html: serializeCmsJson(fields) }} />;
}

/** Prevent mounting the new route with a previous page's data or outdated literals. */
export function CmsRouteBoundary({ children }: { children: ReactNode }) {
  const [location] = useLocation();
  const path = canonicalPath(location);
  const [loadedPath, setLoadedPath] = useState(path);
  useEffect(() => {
    // Includes history Back after a SPA visit to a product: it must also load
    // the current HTML/JS pair, even when returning to the initial document URL.
    if (!isPrerenderMode && !snapshot?.preview && path !== loadedPath && cmsPageByPath(path)) {
      window.location.replace(path); return;
    }
    if (!cmsPageByPath(path) && !snapshot?.preview) setCmsSnapshot(null);
    // An active page always carries its revision in server HTML. A document
    // without a seed is an inactive static page and needs no CMS API request.
    setLoadedPath(path);
  }, [path, loadedPath]);
  if (loadedPath !== path) return <div aria-label="Wczytywanie strony" aria-busy="true" style={{ minHeight: '100vh' }} />;
  return children;
}
